import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Eye, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useHelpCenter } from '../contexts/HelpCenterContext';
import { API_URL } from '../apiBase';
import { mergeTutorialFullView, readStoredFullView } from '../lib/helpTutorialStorage';
import { parseStepQueryParam, StepPositionedBubbles, toYoutubeEmbed, resolveHelpMediaUrl } from '../components/help/HelpTutorialShared';

function isTypingTarget(el) {
  if (!el || !el.tagName) return false;
  const t = el.tagName;
  return t === 'INPUT' || t === 'TEXTAREA' || t === 'SELECT' || el.isContentEditable;
}

export default function TutorialWindowPage() {
  const { tutorialId: rawTutorialId } = useParams();
  const tutorialId = rawTutorialId ? decodeURIComponent(rawTutorialId) : '';
  const [searchParams, setSearchParams] = useSearchParams();
  const stepParam = searchParams.get('step');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshSummary } = useHelpCenter();

  const [tutorial, setTutorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [completePending, setCompletePending] = useState(false);
  const [autoClosePending, setAutoClosePending] = useState(false);
  const [tutorialFinished, setTutorialFinished] = useState(false);

  const reachedLastStepFiredRef = useRef(false);
  const autoCloseTimerRef = useRef(null);

  const resetTransient = useCallback(() => {
    reachedLastStepFiredRef.current = false;
    setAutoClosePending(false);
    setTutorialFinished(false);
    setLoadError(null);
    setStepIndex(0);
    if (autoCloseTimerRef.current) {
      window.clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    resetTransient();
  }, [tutorialId, resetTransient]);

  useEffect(() => {
    if (!tutorialId) {
      setTutorial(null);
      setLoadError('Tutoriel introuvable.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setTutorial(null);
      try {
        const { data } = await axios.get(
          `${API_URL}/api/help-center/tutorials/${encodeURIComponent(tutorialId)}`,
        );
        if (cancelled) return;
        if (!data?.tutorial) {
          setLoadError('Réponse serveur inattendue.');
          setTutorial(null);
          return;
        }
        setTutorial(data.tutorial);
      } catch {
        if (cancelled) return;
        setLoadError('Impossible de charger ce tutoriel ou accès refusé.');
        setTutorial(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tutorialId]);

  const etapes = tutorial?.etapes || [];
  const isEtapes = tutorial?.mode === 'etapes' && etapes.length > 0;
  const lastStep = isEtapes && stepIndex >= etapes.length - 1;
  const embed = tutorial?.video_url ? toYoutubeEmbed(tutorial.video_url) : null;
  const currentStepScreenshot = isEtapes && etapes[stepIndex]
    ? resolveHelpMediaUrl(etapes[stepIndex].screenshot_url)
    : null;

  /** Hydratation étape depuis l’URL (entier ≥ 1, sinon étape 1). */
  useEffect(() => {
    if (!tutorial || !isEtapes) return;
    const idx = parseStepQueryParam(stepParam, etapes.length);
    setStepIndex(idx);
  }, [tutorial?.id, isEtapes, etapes.length, stepParam]);

  const syncStepToUrl = useCallback(
    (idx) => {
      if (!isEtapes || etapes.length === 0) return;
      const clamped = Math.max(0, Math.min(idx, etapes.length - 1));
      const next = String(clamped + 1);
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('step', next);
        return p;
      }, { replace: true });
    },
    [isEtapes, etapes.length, setSearchParams],
  );

  const markCompleteApi = useCallback(async () => {
    if (!tutorial?.id) return;
    await axios.post(`${API_URL}/api/help-center/tutorials/${tutorial.id}/complete`);
    if (user?.id) {
      mergeTutorialFullView(user.id, tutorial.id);
    }
    await refreshSummary();
    setTutorial((t) => (t ? { ...t, completed: true, badge_nouveau: false, badge_mis_a_jour: false } : t));
  }, [tutorial?.id, user?.id, refreshSummary]);

  /** À l’entrée sur la dernière étape : marquer comme vu sans bloquer l’UI. */
  useEffect(() => {
    if (!tutorial?.id || !user?.id || !isEtapes) return;
    const isLast = stepIndex >= etapes.length - 1;
    if (!isLast) {
      reachedLastStepFiredRef.current = false;
      return;
    }
    if (tutorial.completed) return;
    if (reachedLastStepFiredRef.current) return;
    reachedLastStepFiredRef.current = true;
    axios
      .post(`${API_URL}/api/help-center/tutorials/${tutorial.id}/complete`)
      .then(() => {
        mergeTutorialFullView(user.id, tutorial.id);
        setTutorial((t) => (t ? { ...t, completed: true, badge_nouveau: false, badge_mis_a_jour: false } : t));
        refreshSummary();
      })
      .catch(() => {});
  }, [stepIndex, tutorial, isEtapes, etapes.length, user?.id, refreshSummary]);

  const isTutorialViewed = Boolean(
    tutorial?.completed ||
      (user?.id && tutorial?.id && readStoredFullView(user.id)[tutorial.id]),
  );

  const runAutoCloseSequence = useCallback(async () => {
    setAutoClosePending(true);
    await new Promise((r) => requestAnimationFrame(r));
    window.close();
    autoCloseTimerRef.current = window.setTimeout(() => {
      autoCloseTimerRef.current = null;
      if (document.visibilityState === 'visible') {
        setAutoClosePending(false);
        setTutorialFinished(true);
      }
    }, 850);
  }, []);

  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current) {
        window.clearTimeout(autoCloseTimerRef.current);
      }
    };
  }, []);

  const handleFinalComplete = async () => {
    if (completePending || !tutorial?.id) return;
    setCompletePending(true);
    try {
      await markCompleteApi();
      await runAutoCloseSequence();
    } finally {
      setCompletePending(false);
    }
  };

  const handleVideoEndedOrMark = async () => {
    if (completePending || !tutorial?.id) return;
    if (tutorial.completed) {
      await runAutoCloseSequence();
      return;
    }
    setCompletePending(true);
    try {
      await markCompleteApi();
      await runAutoCloseSequence();
    } finally {
      setCompletePending(false);
    }
  };

  const handleLeaveFinished = () => {
    if (autoCloseTimerRef.current) {
      window.clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    setAutoClosePending(false);
    setTutorialFinished(false);
    window.close();
    window.setTimeout(() => navigate('/dashboard'), 0);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!isEtapes || etapes.length === 0) return;
      if (isTypingTarget(document.activeElement)) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setStepIndex((i) => {
          const n = Math.max(0, i - 1);
          syncStepToUrl(n);
          return n;
        });
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setStepIndex((i) => {
          const n = Math.min(etapes.length - 1, i + 1);
          syncStepToUrl(n);
          return n;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isEtapes, etapes.length, syncStepToUrl]);

  const idMismatch = Boolean(tutorial && tutorialId && tutorial.id !== tutorialId);

  const stepTexte = isEtapes && etapes[stepIndex] ? etapes[stepIndex].texte : null;

  const closeOrDashboard = useCallback(() => {
    window.close();
    window.setTimeout(() => navigate('/dashboard'), 0);
  }, [navigate]);

  /** Priorité d’affichage : erreur → repli fermeture → auto-close → chargement / incohérence → UI principale */
  if (loadError) {
    return (
      <div className="flex h-dvh max-h-dvh min-h-0 flex-col items-center justify-center gap-4 overflow-hidden bg-background px-6 text-center">
        <p className="max-w-md text-sm leading-relaxed text-muted-foreground">{loadError}</p>
        <Button type="button" variant="outline" onClick={closeOrDashboard}>
          Fermer
        </Button>
      </div>
    );
  }

  if (tutorialFinished) {
    return (
      <div className="flex h-dvh max-h-dvh min-h-0 flex-col items-center justify-center gap-4 overflow-hidden bg-background px-6 text-center">
        <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
          Parcours terminé. La fermeture automatique de l&apos;onglet n&apos;a pas pu s&apos;effectuer (navigateur ou
          onglet ouvert manuellement). Vous pouvez fermer cet onglet ou retourner à l&apos;application pour accéder au
          centre d&apos;aide.
        </p>
        <Button type="button" variant="outline" onClick={handleLeaveFinished}>
          Fermer
        </Button>
      </div>
    );
  }

  if (autoClosePending) {
    return (
      <div
        className="flex h-dvh max-h-dvh min-h-0 flex-col items-center justify-center gap-3 overflow-hidden bg-background px-6"
        aria-live="polite"
      >
        <div className="spinner" aria-hidden />
        <p className="text-sm text-muted-foreground">Fermeture de l&apos;onglet…</p>
      </div>
    );
  }

  if (loading || !tutorial || idMismatch) {
    return (
      <div className="flex h-dvh max-h-dvh min-h-0 flex-col items-center justify-center overflow-hidden bg-background">
        <div className="spinner" aria-hidden />
      </div>
    );
  }

  return (
    <div className="flex h-dvh max-h-dvh min-h-0 flex-col overflow-hidden bg-background text-foreground">
      <header className="shrink-0 border-b border-border bg-background px-4 py-3 md:px-6 md:py-4">
        <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[1fr_2fr_1fr] md:gap-6">
          <div className="min-w-0 space-y-1 md:pr-2">
            <div className="flex flex-wrap items-start gap-2">
              <h1 className="min-w-0 flex-1 text-base font-semibold text-foreground sm:text-lg">
                {tutorial.titre || 'Tutoriel'}
              </h1>
              <Eye className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2} aria-hidden />
              {isTutorialViewed && (
                <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Parcours terminé</span>
              )}
            </div>
            {tutorial.theme_nom && (
              <p className="text-sm text-muted-foreground">Thème : {tutorial.theme_nom}</p>
            )}
            {tutorial.description && (
              <p className="line-clamp-3 text-xs leading-snug text-muted-foreground sm:line-clamp-4">
                {tutorial.description}
              </p>
            )}
          </div>

          <div className="flex min-w-0 flex-col items-center gap-3 border-border py-2 md:border-x md:px-4 md:py-0">
            {isEtapes && etapes.length > 0 && (
              <>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Étape {stepIndex + 1} / {etapes.length}
                </p>
                {stepTexte && (
                  <div className="max-h-[min(22dvh,180px)] w-full overflow-y-auto text-center sm:max-h-[min(25dvh,220px)]">
                    {String(stepTexte).includes('<') ? (
                      <div
                        className="text-sm text-foreground [&_a]:underline"
                        dangerouslySetInnerHTML={{ __html: String(stepTexte) }}
                      />
                    ) : (
                      <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">{String(stepTexte)}</p>
                    )}
                  </div>
                )}
                <div className="flex w-full max-w-md flex-wrap items-center justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-border bg-transparent text-foreground disabled:opacity-40"
                    disabled={stepIndex === 0}
                    onClick={() => {
                      setStepIndex((i) => {
                        const n = Math.max(0, i - 1);
                        syncStepToUrl(n);
                        return n;
                      });
                    }}
                  >
                    <ChevronLeft className="mr-1 h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
                    Précédent
                  </Button>
                  {!lastStep ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="default"
                      onClick={() => {
                        setStepIndex((i) => {
                          const n = Math.min(etapes.length - 1, i + 1);
                          syncStepToUrl(n);
                          return n;
                        });
                      }}
                    >
                      Suivant
                      <ChevronRight className="ml-1 h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="default"
                      disabled={completePending}
                      className="font-semibold shadow-sm"
                      onClick={handleFinalComplete}
                    >
                      Parcours terminé
                    </Button>
                  )}
                </div>
              </>
            )}
            {!isEtapes && (
              <p className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Lecture vidéo
              </p>
            )}
          </div>

          <div className="flex justify-end md:items-start md:pt-0">
            <button
              type="button"
              onClick={closeOrDashboard}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border text-muted-foreground transition-colors hover:border-muted-foreground/50 hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-label="Fermer"
            >
              <X className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
            </button>
          </div>
        </div>
      </header>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-1 pb-1 pt-0.5 sm:px-3 sm:pb-2 sm:pt-1">
        {isEtapes && etapes[stepIndex] ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card">
            {currentStepScreenshot ? (
              <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden bg-muted/50 p-1 sm:p-2">
                <div className="relative max-h-full max-w-full min-w-0">
                  <img
                    src={currentStepScreenshot}
                    alt=""
                    className="block max-h-full max-w-full object-contain"
                  />
                  <StepPositionedBubbles etape={etapes[stepIndex]} />
                </div>
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
                Aucune capture pour cette étape.
              </div>
            )}
          </div>
        ) : null}

        {!isEtapes && tutorial.video_url && (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col items-stretch justify-center gap-3 overflow-hidden sm:gap-4">
            <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden rounded-lg bg-black">
              {embed ? (
                <iframe
                  title={tutorial.titre}
                  src={embed}
                  className="aspect-video h-auto max-h-full w-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video
                  src={tutorial.video_url}
                  controls
                  className="max-h-full w-full object-contain"
                  onEnded={handleVideoEndedOrMark}
                />
              )}
            </div>
            <Button
              type="button"
              variant="default"
              className="shrink-0"
              disabled={completePending}
              onClick={tutorial.completed ? closeOrDashboard : handleVideoEndedOrMark}
            >
              {tutorial.completed ? 'Fermer' : 'Marquer comme vu'}
            </Button>
          </div>
        )}

        {!isEtapes && !tutorial.video_url && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
            <p className="max-w-md text-sm text-muted-foreground">
              Aucune vidéo n&apos;est associée à ce tutoriel. Marquez-le comme vu lorsque vous avez terminé.
            </p>
            <Button
              type="button"
              variant="default"
              disabled={completePending}
              onClick={tutorial.completed ? closeOrDashboard : handleVideoEndedOrMark}
            >
              {tutorial.completed ? 'Fermer' : 'Marquer comme vu'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
